import {
  closeCurrentDialog,
  Dropdown,
  DropdownItem,
  openDeleteDialog,
  openDialog,
  Tooltip,
} from 'platform/components';
import {Link} from 'platform/foundation';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery, useUpdateKeyLocationMutation} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {handleApiError} from '../../../utils/handleApiError';
import {NoPermissionTooltip} from '../../NoPermissionTooltip/NoPermissionTooltip';
import {KeyLocationForm} from './KeyLocationForm';

export interface KeyLocationProps extends TestIdProps {
  recordId: string;
}

export function KeyLocation(props: KeyLocationProps) {
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.recordId});
  const [updateKeyLocation] = useUpdateKeyLocationMutation();

  const {data: vehicleParticipationId} = useGetParticipationQuery({
    recordId: props.recordId,
    resourceId: EntityResourceIds.vehicle,
  });

  const [canChangeVehicleKeyNumber] = usePermissions({
    permissionKeys: ['vehicleChangeKeyNumber'],
    scopes: {
      vehicleChangeKeyNumber: {participation: vehicleParticipationId},
    },
  });

  const handleUpdateKeyLocation = async (keyLocation: string | null) => {
    if (!vehicle) {
      return;
    }

    await updateKeyLocation({
      vehicleId: props.recordId,
      updateVehicleKeyLocationRequestBody: {
        keyLocation,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const openKeyLocationDialog = (defaultValue?: string | Nullish) =>
    openDialog(
      <KeyLocationForm
        onClose={closeCurrentDialog}
        onSubmit={({keyLocation}) => handleUpdateKeyLocation(keyLocation ?? null)}
        keyLocation={defaultValue}
        data-testid={suffixTestId('key-form', props)}
      />,
      {
        size: 'small',
        title: defaultValue
          ? i18n.t('entity.vehicle.labels.editVehicleKey')
          : i18n.t('entity.vehicle.labels.addVehicleKey'),
      }
    );

  const onKeyLocationClick = () => {
    if (vehicle?.keyLocation) {
      return;
    }

    openKeyLocationDialog();
  };

  const handleDelete = () => {
    openDeleteDialog({onConfirm: () => handleUpdateKeyLocation(null)});
  };

  return (
    <Dropdown
      dropdownControl={
        <NoPermissionTooltip shouldShowTooltip={!canChangeVehicleKeyNumber}>
          <Tooltip
            label={i18n.t('entity.controls.labels.key.tooltip')}
            isDisabled={!canChangeVehicleKeyNumber}
            childrenWrapperDisplay="flex"
          >
            <Link
              size="small"
              leftIcon="communication/vpn_key"
              title={vehicle?.keyLocation ?? i18n.t('entity.vehicle.labels.keyLocation')}
              onClick={onKeyLocationClick}
              isDisabled={!canChangeVehicleKeyNumber}
              data-testid={suffixTestId('key', props)}
            />
          </Tooltip>
        </NoPermissionTooltip>
      }
      isDisabled={!canChangeVehicleKeyNumber}
      data-testid={suffixTestId('key-dropdown', props)}
      isOpen={vehicle?.keyLocation ? undefined : false}
    >
      <DropdownItem
        label={i18n.t('general.actions.edit')}
        onClick={() => openKeyLocationDialog(vehicle?.keyLocation)}
        data-testid={suffixTestId('key-dropdown-edit', props)}
      />
      <DropdownItem
        label={i18n.t('general.actions.delete')}
        onClick={handleDelete}
        data-testid={suffixTestId('key-dropdown-delete', props)}
      />
    </Dropdown>
  );
}
